import axios from "axios";
import { useCallback, useState } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/events";

const api = axios.create({
  baseURL: baseUrl,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const useEvents = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAllEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/");
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const getEventById = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${id}`);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const createEvent = useCallback(async (event) => {
    setLoading(true);
    try {
      const { data } = await api.post("/", event);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const updateEvent = useCallback(async (id, event) => {
    setLoading(true);
    try {
      const { data } = await api.put(`/${id}`, event);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const deleteEvent = useCallback(async (id) => {
    setLoading(true);
    try {
      await api.delete(`/${id}`);
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const getRegistrations = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${id}/registrations`);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const deleteRegistration = useCallback(async (id) => {
    setLoading(true);
    try {
      await api.delete(`/registrations/${id}`);
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  // notify=true stuurt bij het op "betaald" zetten alsnog de bevestigingsmail
  // die de deelnemer bij een online betaling pas op dat moment krijgt.
  const updateRegistrationStatus = useCallback(async (id, status, notify = false) => {
    setLoading(true);
    try {
      const { data } = await api.patch(`/registrations/${id}/status`, {
        status,
        ...(notify ? { notify: true } : {}),
      });
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  return {
    loading,
    error,
    getAllEvents,
    getEventById,
    createEvent,
    updateEvent,
    deleteEvent,
    getRegistrations,
    deleteRegistration,
    updateRegistrationStatus,
  };
};

export default useEvents;
